import { ref, onMounted, onUnmounted } from "vue";
import { useRouter, type RouteLocationNormalized } from "vue-router";

/**
 * Check if the current page is the first page in navigation history
 * This is useful for determining if back navigation should go to home instead of leaving the app
 */
export function isFirstPage(): boolean {
  const historyState = window.history.state;

  // No history state means we can't determine, assume first page
  if (!historyState || typeof historyState.position !== "number") {
    return true;
  }

  const position = historyState.position;
  const backState = historyState.back;

  // Position 0 or 1 with no back state means first page
  // Vue Router starts at position 1 for the first page
  if (position <= 1) {
    // Also check if there's no internal referrer
    const hasInternalReferrer =
      document.referrer && document.referrer.includes(window.location.host);
    if (!hasInternalReferrer) {
      return true;
    }
    // If back is null, we're at the first page
    if (backState === null) {
      return true;
    }
    // If back is root, we're effectively at first page
    if (backState === "/" || backState === "") {
      return true;
    }
  }

  return false;
}

export function useNavigationHistory() {
  const router = useRouter();
  const canGoBack = ref(false);

  function updateCanGoBack(to?: RouteLocationNormalized) {
    // Check if we can go back by looking at the current history state
    // Vue Router sets a position in history.state when navigating
    if (
      !window.history.state ||
      typeof window.history.state.position !== "number"
    ) {
      canGoBack.value = false;
      return;
    }

    const position = window.history.state.position;
    const backState = window.history.state.back;

    // If we're at position 0, definitely can't go back
    if (position === 0) {
      canGoBack.value = false;
      return;
    }

    // If back is null, we can't go back regardless of position
    // This happens after auth redirects where history is replaced
    if (backState === null) {
      canGoBack.value = false;
      return;
    }

    // If we're at position 1, check if we came from the root redirect
    // The root path "/" redirects to the home page, so position 1 from root means no real history
    if (position === 1) {
      if (backState === "/" || backState === "") {
        canGoBack.value = false;
        return;
      }
    }

    // Otherwise, we have real navigation history
    canGoBack.value = true;
  }

  /**
   * Smart back navigation that goes to home if this is the first page
   * instead of leaving the app
   */
  function goBack(fallbackPath = "/") {
    if (canGoBack.value) {
      router.back();
    } else {
      // No history to go back to, navigate to home
      router.replace(fallbackPath);
    }
  }

  let unwatchRouter: (() => void) | null = null;

  onMounted(() => {
    // Check initial state
    updateCanGoBack(router.currentRoute.value);

    // Listen for route changes to update the state
    unwatchRouter = router.afterEach((to) => {
      updateCanGoBack(to);
    });
  });

  onUnmounted(() => {
    if (unwatchRouter) {
      unwatchRouter();
    }
  });

  return {
    canGoBack,
    goBack,
    isFirstPage: () => !canGoBack.value,
  };
}
